import { BreadcrumbsService } from './../../services/breadcrumb.service';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { NgIf } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';

import { SubscriberBaseDirective } from '@app/shared/components/subscriber-base/subscriber-base.directive';
import { twMerge } from '../../utils/tw-merge';
import { LayoutAspect } from './layout.model';
import { HeaderComponent } from './components/header.component';
import { SidebarComponent } from './components/sidebar.component';
import { FooterComponent } from './components/footer.component';
import { SkipNavComponent } from '../skip-nav/skip-nav.component';
import { BreadcrumbComponent } from '../breadcrumb/breadcrumb.component';
import { EventBus } from '@app/shared/services/event-bus';
import { startWith } from 'rxjs';
import { MCE_EVENTS } from '@app/tiny-mce/_tiny-mce.module';

const APOLLO_SIDE_BAR_EXPANDED = 'apollo-sidebar-expanded';

/**
 * Layout component (v3 === Oct 2024 release)
 */
@Component({
    // eslint-disable-next-line @angular-eslint/component-selector
    selector: 'da-layout-v3',
    changeDetection: ChangeDetectionStrategy.OnPush,
    template: `
    <div
      [style]="{
        '--apollo-layout-background':
          configuration?.brand?.colors?.background || '#ffffff',
        '--apollo-layout-text':
          configuration?.brand?.colors?.text ||
          'var(--apollo-color-neutral-800)',
        '--apollo-layout-highlight':
          configuration?.brand?.colors?.highlight ||
          'var(--apollo-color-blue-800)',
        '--apollo-layout-separator':
          configuration?.brand?.colors?.separator ||
          'var(--apollo-color-neutral-200)',
      }"
      [class]="
        twMerge(
          'tw-bg-neutral-50',
          previewOnly ? 'tw-pointer-events-none' : '',
          className
        )
      "
    >
      <!-- Header -->
      <da-header
        *ngIf="showControls"
        [brand]="configuration?.brand"
        [features]="configuration?.features"
        [navigation]="configuration?.navigation"
        [className]="
          twMerge(
            'tw-transition-[margin] tw-reset tw-z-20',
            sidebarExpanded
              ? 'lg:tw-ml-[200px] rtl:lg:tw-ml-0 rtl:lg:tw-mr-[200px]'
              : 'lg:tw-ml-20 rtl:lg:tw-ml-0 rtl:lg:tw-mr-20',
            previewOnly ? 'tw-absolute' : ''
          )
        "
        [previewOnly]="previewOnly"
        (search)="this.search.emit($event)"
        (addContent)="this.addContent.emit($event)"
      ></da-header>

      <!-- Skip To Main Content Link -->
      <da-skip-nav
        class="tw-absolute tw-z-30"
        [containerName]="'main-content'"
        [linkText]="i18n.dgSkipNav_SkipToMainContent"
      ></da-skip-nav>

      <!-- Sidebar -->
      <da-sidebar
        *ngIf="showControls"
        [brand]="configuration?.brand"
        [features]="configuration?.features"
        [navigation]="configuration?.navigation"
        [sidebarExpanded]="sidebarExpanded"
        [className]="
          twMerge(
            'tw-transition-[width] tw-hidden lg:tw-block tw-reset tw-z-20',
            sidebarExpanded ? 'lg:tw-w-[200px]' : 'lg:tw-w-20',
            previewOnly ? 'tw-absolute' : ''
          )
        "
        [previewOnly]="previewOnly"
        (toggleExpanded)="toggleSidebarCollapse()"
        (switchRole)="this.switchRole.emit($event)"
      ></da-sidebar>

      <!-- Main Content -->
      <main
        id="main-content"
        [class]="
          twMerge(
            'tw-box-border tw-flex tw-flex-col tw-pt-16 tw-transition-[margin]',
            sidebarExpanded
              ? 'lg:tw-ml-[200px] rtl:lg:tw-ml-0 rtl:lg:tw-mr-[200px]'
              : 'lg:tw-ml-20 rtl:lg:tw-ml-0 rtl:lg:tw-mr-20',
            previewOnly ? 'tw-min-h-[544px]' : 'tw-min-h-dvh'
          )
        "
      >
        @if (breadcrumbs().length > 0) {
          <div class="tw-reset">
            <div class="tw-mx-auto tw-max-w-[90rem]">
              <da-breadcrumb [items]="breadcrumbs()" />
            </div>
          </div>
        }
        <div class="tw-relative tw-grow">
          <ng-content></ng-content>
        </div>

        <!-- Footer -->
        <da-footer
          className="tw-reset"
          [footerBranding]="configuration?.features?.footerBranding"
        />
      </main>
    </div>
  `,
    imports: [
        NgIf,
        HeaderComponent,
        SidebarComponent,
        FooterComponent,
        SkipNavComponent,
        BreadcrumbComponent,
    ]
})
export class LayoutComponent extends SubscriberBaseDirective implements OnInit {
  @Input() public className = '';
  @Input() public configuration?: LayoutAspect; // `learner` or `admin` aspect of the LayoutConfiguration
  @Input() public previewOnly = false;
  @Output() public search = new EventEmitter<MouseEvent>();
  @Output() public addContent = new EventEmitter<MouseEvent>();
  @Output() public switchRole = new EventEmitter<MouseEvent>();

  /** Show or hide controls based on the mobile query param */
  public showControls: boolean = true;
  public i18n: Record<string, string> = this.translateService.instant([
    'dgSkipNav_SkipToMainContent',
  ]);

  public sidebarExpanded: boolean = true;
  public twMerge = twMerge;

  public get breadcrumbs() {
    return this.breadcrumbsService.breadcrumbs;
  }

  constructor(
    private route: ActivatedRoute,
    private translateService: TranslateService,
    private breadcrumbsService: BreadcrumbsService,
    private eventBus: EventBus,
    private cdr: ChangeDetectorRef
  ) {
    super();
    this.restoreSidebarCollapse();
  }

  public ngOnInit(): void {
    // Subscribe to queryParams to handle mobile=1
    this.route.queryParams
      .pipe(this.takeUntilDestroyed())
      .subscribe((params) => {
        const mobile = params['mobile'];
        // Hide the layout if mobile=1
        this.showControls = mobile !== '1';
      });

    this.eventBus
      .observableFor<{ isFullScreen: boolean }>(
        MCE_EVENTS.FULL_SCREEN_STATE_CHANGED
      )
      .pipe(startWith(null), this.takeUntilDestroyed())
      .subscribe((toggleEvent) => {
        const isFullScreen = toggleEvent?.isFullScreen === true;
        this.showControls = !isFullScreen;
        this.cdr.detectChanges();
      });
  }

  // ************************************************
  // Sidebar state persistence
  // ************************************************

  public toggleSidebarCollapse(): void {
    this.sidebarExpanded = !this.sidebarExpanded;
    localStorage.setItem(
      APOLLO_SIDE_BAR_EXPANDED,
      this.sidebarExpanded.toString()
    );
  }

  public restoreSidebarCollapse(): void {
    const isExpanded = localStorage.getItem(APOLLO_SIDE_BAR_EXPANDED);
    this.sidebarExpanded = isExpanded !== 'false';
  }
}
